import type {MouseEvent, ReactElement} from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useCopyAiChatImage} from '@Components/ai-chat-window/hooks/useCopyAiChatImage';
import {CopyAiChatImageStatus} from '@Components/ai-chat-window/hooks/useCopyAiChatImage.types';
import {TooltipPlacement} from '@Components/icon-with-tooltip/icon-with-tooltip.types';
import {deriveArrowPositionFromPlacement, getTooltipOverlayClassName} from '@Components/icon-with-tooltip/icon-with-tooltip-helper';
import {isTouchDevice} from '@Utils/browser.util';
import type {CopyAiChatImageButtonProps} from './copy-ai-chat-image-button.types';
import {ICON_BY_COPY_STATUS, LABEL_KEY_BY_COPY_STATUS} from './copy-ai-chat-image-button.types';
import styles from './copy-ai-chat-image-button.module.scss';

export function CopyAiChatImageButton({
  hashedId,
  source,
  iconProps,
  tooltipPlacement = TooltipPlacement.TOP,
  tooltipContainer,
}: CopyAiChatImageButtonProps): ReactElement | null {
  const {canCopyImage, copyStatus, copyImage} = useCopyAiChatImage({hashedId, source});

  if (!canCopyImage) {
    return null;
  }

  const isCopying = copyStatus === CopyAiChatImageStatus.COPYING;
  const label = window.i18next.t(LABEL_KEY_BY_COPY_STATUS[copyStatus]);

  const getTooltipClassName = (): string => {
    return `${getTooltipOverlayClassName(false, deriveArrowPositionFromPlacement(tooltipPlacement))} ${isTouchDevice() ? '_invisible' : ''}`;
  };

  const onCopyClick = (clickEvent: MouseEvent): void => {
    clickEvent.stopPropagation();
    copyImage();
  };

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      overlay={<Tooltip className={getTooltipClassName()}>{label}</Tooltip>}
      delay={{show: 0, hide: 0}}
      placement={tooltipPlacement}
      container={tooltipContainer}
    >
      <ClickableDiv onClick={onCopyClick} aria-label={label} aria-busy={isCopying}>
        <Icon size={IconSize.SIZE_ICON_16} loaderClassName={styles.copyImageLoader} {...iconProps} icon={ICON_BY_COPY_STATUS[copyStatus]} isLoading={isCopying} ariaHidden />
      </ClickableDiv>
    </OverlayTrigger>
  );
}
